<template>
  <div class="InvoicePeriod" v-if="invoice">
    <h3>
      Service Period
      {{ serviceDate }}
    </h3>

    <table class="InvoicePeriod__table InvoicePeriod__table__main table">
      <colgroup>
        <col>
        <col style="width:150px">
      </colgroup>

      <thead>
        <tr>
          <th>Item description</th>
          <th class="text-end">Total</th>
        </tr>
      </thead>

      <tbody>
        <tr>
          <td>
            Account Balance (Last Statement)
            <time>{{ statementDate }}</time>
          </td>
          <td class="text-end">
            ${{ formatDecimal(invoice.statements.previous) }}
          </td>
        </tr>

        <tr>
          <td>
            Period deposit
          </td>
          <td class="text-end">
            ${{ formatDecimal(invoice.period.deposit) }}
          </td>
        </tr>

        <tr>
          <td>
            Period withdraw
          </td>
          <td class="text-end">
            ${{ formatDecimal(invoice.period.withdraw) }}
          </td>
        </tr>

        <tr>
          <td>
            Account Balance
            <time>{{ date }}</time>
          </td>
          <td class="text-end">
            ${{ formatDecimal(invoice.statements.current) }}
          </td>
        </tr>
      </tbody>
    </table>

    <table class="InvoicePeriod__table InvoicePeriod__table__footer table">
      <colgroup>
        <col>
        <col style="width:200px">
        <col style="width:150px">
      </colgroup>
      <tfoot>
        <tr>
          <td class="InvoicePeriod__table__notes" rowspan="6">
            <div class="InvoicePeriod__notes">
              <p>
                <strong>
                  New Equity / Statement {{ date }} = ${{ formatDecimal(invoice.statements.current) }} - ${{ formatDecimal(invoice.due.performance_fee + invoice.due.quaterly_fee) }} = ${{ formatDecimal(invoice.statements.new) }}
                  (settlement from your CFT trading wallet).
                </strong>
              </p>

              <p>
                <b>Software Infrastructure &amp; Maintenance Fee — {{ formatRate(annualRate) }}% p.a. ({{ formatRate(quarterlyRate) }}% per quarter)</b>
                Fee for software licensing and access, API integration and maintenance, execution-infrastructure provisioning, monitoring and operational support. Sized on the Configured Notional for the module — the client's net contributed capital (total deposits less total withdrawals, excluding performance) — adjusted per the client's own written deposit and withdrawal requests, taken at 00:00 UTC on the first day of the quarter. Not a management, advisory, incentive or custody fee; does not constitute asset management. Client assets remain in client-controlled, exchange-hosted accounts; C.FT Ltd holds no custody, withdrawal or transfer authority and exercises no discretion.
              </p>
              <p>
                <b>Performance-Based Software Fee</b>
                is calculated monthly and only above the High-Water Mark, with deposits, withdrawals and capital transfers neutralised. Applies only where the module records Net Positive Performance above its High-Water Mark for the period.
              </p>
              <p>
                By proceeding with settlement, the client acknowledges that:<br/>
                ✓ All fees are non-refundable.<br/>
                ✓ The services rendered under this invoice are software services and licensed module use only.<br/>
                ✓ Any dispute relating to this invoice must be raised within 7 days.
              </p>
              <p class="InvoicePeriod__disclaimer">Capital Flow Tech is a commercial brand operated by C.FT Ltd, providing software infrastructure and API-based algorithmic trading technology for digital-asset markets. CFT does not provide investment advice, portfolio management, asset management, brokerage, custody, deposit-taking or discretionary financial services. Client assets remain in client-controlled accounts, with no custody and no withdrawal authority granted to CFT.</p>
            </div>
          </td>

          <td>
            Gross Performance
          </td>

          <td class="text-end">
            ${{ formatDecimal(invoice.period.performance) }}
          </td>
        </tr>

        <tr>
          <td>Performance-Based Software Fee {{ formatRate(invoice.settings.performance_fee) }}% of Gross Performance above HWM</td>
          <td class="text-end">
            ${{ formatDecimal(invoice.due.performance_fee) }}
          </td>
        </tr>

        <tr>
          <td>
            Software Infrastructure &amp; Maintenance Fee {{ formatRate(invoice.settings.quaterly_fee) }}% per quarter, covering software licensing, API integration and maintenance, execution-infrastructure provisioning and operational support
            <time v-if="invoice.due.quaterly_fee !== null">{{ periodDates }}</time>
          </td>
          <td class="text-end">
            ${{ formatDecimal(invoice.due.quaterly_fee || 0) }}
          </td>
        </tr>

        <tr>
          <td>Total</td>
          <td class="text-end">
            ${{ formatDecimal(invoice.due.performance_fee + invoice.due.quaterly_fee) }}
          </td>
        </tr>

        <tr>
          <th>Total Due</th>
          <th class="text-end">
            ${{ formatDecimal(invoice.due.performance_fee + invoice.due.quaterly_fee) }}
          </th>
        </tr>

        <!-- The VAT note reads the LIVE global settings blob. That is a DECISION,
             not an oversight, and not a pending item.

             invoice.settings.vat_info is frozen at generation on all 5,574 invoices
             and the API sanitizer already passes it through, so pointing this row at
             it is a one-line change. It is also structurally the same finding as the
             settlement address (d9bfd06), which is why an audit of this file keeps
             arriving here and filing it.

             CLOSED 2026-08-29. The frozen value differs from the live one on 3,887
             invoices, and 553 of those pre-date the Bahamian entity that has been the
             invoicing entity since 2022 -- their frozen note carries a prior entity's
             VAT wording. Restoring it would surface a superseded entity's tax basis on
             the platform today: confusing at best. The display stays as it is.

             So: not a bug, and not deferred. Reopening it is a business decision about
             entity history, not a code fix. -->
        <tr class="InvoicePeriod__table__vat" v-if="invoice.global_settings.invoice_vat_info">
          <td colspan="2">
            {{ invoice.global_settings.invoice_vat_info }}
          </td>
        </tr>
      </tfoot>
    </table>
  </div>
</template>

<script>
  import { formatDate, hasDate } from '@/utils/date'
  import { formatDecimal, formatRate } from '@/utils/number'
  import { addDays } from 'date-fns'

  export default {
    props: {
      invoice: {
        type: Object,
        default: null
      }
    },

    computed: {
      /* Both rates come from the SAME frozen field on THIS invoice, never from
         the client record. 72 of 103 clients have more than one distinct frozen
         rate pair across their history, and one client whose special_* are both
         null holds 25 invoices frozen at 30% while the engine would resolve 20%
         for them today. Reading the client here would restate that whole
         archive -- the settlement-address bug, on the fee. */
      quarterlyRate() {
        return this.invoice.settings.quaterly_fee
      },

      /* COMPUTED, never written.
         The old text hardcoded "2% p.a. (0.5% per quarter)": a fixed annual
         figure asserting a fixed relation to a fixed quarterly one. Driving the
         quarterly figure from the invoice while leaving a written annual beside
         it would be the same bug in a new place -- 0.123 and 0.3 both appear in
         the frozen history and neither annualises to 2%.
         Four quarters, simple multiplication, the same relation the old string
         asserted, now applied to whatever this invoice actually froze. */
      annualRate() {
        return this.invoice.settings.quaterly_fee * 4
      },

      /* THIS IS A CLIENT-FACING DOCUMENT, SO AN ABSENT DATE PRINTS AS AN ABSENCE.
         formatDate's third argument is what an absence should READ as. The default is ''
         and a blank is fine on an admin screen, but not here: under a heading like
         "Service period", empty space reads as a rendering that failed rather than as a
         value nobody recorded. An em dash says the second thing.

         DEFENCE IN DEPTH, NOT A LIVE FIX -- and it is worth being precise about which.
         Measured 2026-09-03: 0 of 5,595 invoices carry a null or missing `period.date`,
         and sanitizers.js:319-321 backfills a falsy one from firstDayInPreviousMonth()
         before the payload is ever sent, so a null cannot reach this component through the
         only invoice serializer there is. Two independent reasons no client has seen a
         wrong period. This guards the day one of them stops being true. */
      date() {
        return formatDate(this.invoice.date, 'MMMM do, yyyy', '—')
      },

      statementDate() {
        return formatDate(this.invoice.period.date, 'MMMM do, yyyy', '—')
      },

      serviceDate() {
        return formatDate(this.invoice.period.date, 'MMMM yyyy', '—')
      },

      /* The one call site the utility CANNOT fully protect on its own. It builds a raw
         `new Date()` first, so a null arrives at formatDate already coerced to an epoch
         Date object -- caught by the epoch-0 check in utc(), but only for the first half.
         `addDays(epoch, 63)` is 5 March 1970, a perfectly ordinary date no guard would
         reject, so the range would have printed "— - March 1970". The absence has to be
         tested on the source value, before any arithmetic touches it. */
      periodDates() {
        if (!hasDate(this.invoice.period.date)) {
          return '—'
        }

        const date = new Date(this.invoice.period.date)

        return formatDate(date, 'MMMM yyyy') + ' - ' + formatDate(addDays(date, 63), 'MMMM yyyy')
      }
    },

    methods: {
      formatDate,
      formatDecimal,
      formatRate
    }
  }
</script>

<style lang="sass" scoped>
  @import '@/sass/styles.vars.all.sass'

  .InvoicePeriod
    break-after: avoid-page
    padding: $spacer $spacer-5

    h3
      font-size: 1.8em

  .InvoicePeriod__table
    vertical-align: middle
    font-size: 1.1em

    thead
      td, th
        background: $primary
        color: white

    tbody
      th, td
        font-weight: bold

      tr:last-child
        th, td
          border-bottom: 0

      time
        display: block
        font-weight: normal

    tfoot
      th, td
        font-weight: bold
        color: $primary

      td:last-child
        color: $body-color

      th
        color: white
        background: $primary
        text-transform: uppercase
        font-size: 1.2em

  tbody, tfoot
    tr
      height: 4em

  .InvoicePeriod__table__notes
    border-bottom-color: transparent
    vertical-align: top

  .InvoicePeriod__notes
    font-weight: normal
    font-size: 9px
    padding-top: $spacer-2
    padding-right: $spacer
    color: $body-color

    p:last-child
      margin: 0

  .InvoicePeriod__table
    margin: 0

  .InvoicePeriod__table__footer
    border-top: 1px solid $primary

    time
      display: block
      font-weight: normal

  .InvoicePeriod__table__main
    thead
      th
        background: $tertiary

    tr:nth-child(even)
      background: $gray-100

  .InvoicePeriod__table__vat
    th, td
      font-weight: normal !important
      color: $body-color

  .InvoicePeriod__disclaimer
    font-size: 8px
    font-style: italic
    color: $gray-700
    margin-top: $spacer-2
</style>
